import React, { useContext } from 'react';
import { Col, Row, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AuthContext, { useAuthContext } from 'context/authContext';
import StatsCards from './StatsCards';
import RecentDonationsTable from './RecentDonationsTable';
import IncompleteDonations from './IncompleteSessions';
import RemindersCalendar from './RemindersCalendar';
import dayjs from 'dayjs';
import { formatMoney } from 'helpers/utils';
import SoftBadge from 'components/common/SoftBadge';

// Exported for use in donations page
export const organizationOrdersTableColumns = [
  {
    accessor: 'simple_id',
    Header: 'Ref',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const { id, donar_id, simple_id } = rowData.row.original;
      return <Link to={`/donors/${donar_id}/giving/${id}`}>{simple_id}</Link>;
    }
  },
  {
    accessor: 'name',
    Header: 'Donor Name',
    cellProps: {
      className: 'py-2'
    }
  },
  {
    accessor: 'created_at',
    Header: 'Date/Time',
    cellProps: {
      className: 'fw-medium py-2'
    },
    Cell: rowData => {
      const { created_at } = rowData.row.original;
      return dayjs(created_at).format('DD/MM/YYYY, HH:mm');
    }
  },
  {
    accessor: 'order_details',
    Header: 'Items',
    cellProps: {
      className: 'fw-medium py-2'
    }
  },
  {
    accessor: 'is_recurring',
    Header: 'Type',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const isRecurring = rowData.row.original.is_recurring;
      return (
        <SoftBadge bg={isRecurring ? 'primary' : 'secondary'} pill>
          {isRecurring ? 'Monthly' : 'One-off'}
        </SoftBadge>
      );
    }
  },
  {
    accessor: 'payment_method',
    Header: 'Payment Method',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const method = rowData.row.original.payment_method;
      if (!method) return '-';
      const bg = method === 'Cash' ? 'success' : 'primary';
      return (
        <SoftBadge bg={bg} pill>
          {method}
        </SoftBadge>
      );
    }
  },
  {
    accessor: 'donation_source',
    Header: 'Source',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const source = rowData.row.original.donation_source;
      if (!source) return '-';
      const isOnline = source === 'online';
      return (
        <SoftBadge bg={isOnline ? 'info' : 'warning'} pill>
          {isOnline ? 'Online' : 'Phone'}
        </SoftBadge>
      );
    }
  },
  {
    accessor: 'amount',
    Header: 'Amount',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const { amount } = rowData.row.original;
      return formatMoney(amount);
    }
  },
  {
    accessor: 'payment_status',
    Header: 'Status',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const status = rowData.row.original.payment_status;
      const bgMap = {
        paid: 'success',
        pending: 'warning',
        failed: 'danger',
        refunded: 'info',
        partially_refunded: 'info',
        cancelled: 'secondary'
      };
      const labelMap = {
        partially_refunded: 'Partial Refund'
      };
      const label = labelMap[status] || status;
      return (
        <SoftBadge bg={bgMap[status] || 'primary'} pill>
          {label?.charAt(0).toUpperCase() + label?.slice(1)}
        </SoftBadge>
      );
    }
  }
];

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const { ability } = useAuthContext();

  const canViewDonations =
    ability?.can('read', 'donations') || ability?.can('read', 'allDonations');

  return (
    <>
      {/* Greeting Section */}
      <Card className="mb-3">
        <Card.Body className="py-3">
          <h4 className="text-primary mb-0">
            <FontAwesomeIcon icon="lightbulb" className="me-2" />
            Hello, {user?.first_name}!
          </h4>
        </Card.Body>
      </Card>

      {/* Stats Cards Section */}
      <StatsCards />

      {/* Reminders Calendar Section */}
      <RemindersCalendar />

      {/* Recent Donations & Sessions Section - only show if user has donations permission */}
      {canViewDonations && (
        <Row className="g-3">
          <Col lg={6}>
            <RecentDonationsTable limit={10} />
          </Col>
          <Col lg={6}>
            <IncompleteDonations />
          </Col>
        </Row>
      )}
    </>
  );
};

export default Dashboard;
